import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { STATUSES, STATUS_DOT } from "@/lib/statuses";
import { Plus, Filter, Archive } from "lucide-react";

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/quotes", {
          params: { archived: showArchived ? "true" : "false" },
        });
        setQuotes(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [showArchived]);

  const byStatus = (key) => quotes.filter((q) => q.status === key);

  return (
    <div className="max-w-[1600px] mx-auto fade-up" data-testid="quotes-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">Funil de Vendas</div>
          <h1 className="font-display text-4xl font-black text-zinc-50 tracking-tighter">Cotações</h1>
          <p className="text-zinc-500 mt-2 text-sm">Kanban completo do orçamento à entrega final.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchived((v) => !v)}
            data-testid="toggle-archived-btn"
            className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all border ${
              showArchived
                ? "bg-amber-500/10 border-amber-700/60 text-amber-300 hover:bg-amber-500/20"
                : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            }`}
          >
            <Archive className="w-4 h-4" strokeWidth={1.8} />
            {showArchived ? "Ver ativas" : "Ver arquivadas"}
          </button>
          <button
            onClick={() => navigate("/cotacoes/nova")}
            data-testid="new-quote-btn"
            className="flex items-center gap-2 px-5 py-3 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all"
          >
            <Plus className="w-4 h-4" strokeWidth={2.2} /> Nova Cotação
          </button>
        </div>
      </div>

      {loading && <div className="text-zinc-500">Carregando...</div>}

      <div className="flex gap-4 overflow-x-auto pb-6 -mx-1 px-1" data-testid="kanban-board">
        {STATUSES.map((s) => {
          const items = byStatus(s.key);
          return (
            <div
              key={s.key}
              data-testid={`kanban-column-${s.key}`}
              className="w-[300px] shrink-0 rounded-xl border border-zinc-800/60 bg-[#0c0c0e] flex flex-col"
            >
              <div className="px-4 py-3.5 border-b border-zinc-800/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[s.key]}`} />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-300 font-bold">{s.label}</span>
                </div>
                <span className="text-xs text-zinc-500 font-mono">{items.length}</span>
              </div>
              <div className="p-3 space-y-3 min-h-[120px]">
                {items.length === 0 && (
                  <div className="text-center text-xs text-zinc-700 py-6">—</div>
                )}
                {items.map((q) => (
                  <div
                    key={q.id}
                    onClick={() => navigate(`/cotacoes/${q.id}`)}
                    data-testid={`kanban-card-${q.id}`}
                    className="rounded-lg border border-zinc-800 bg-[#141416] p-3.5 hover:-translate-y-0.5 hover:border-zinc-600 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-mono text-[11px] text-zinc-500">{q.number}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-bold">
                        {q.items?.length || 0} itens
                      </div>
                    </div>
                    <div className="font-medium text-zinc-100 text-sm mb-2 line-clamp-1">
                      {q.client?.name || "—"}
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="text-sm font-bold text-zinc-100">
                        {q.confirmed_total != null
                          ? fmtEUR(q.confirmed_total)
                          : q.total_range && q.total_range.min !== q.total_range.max
                            ? `${fmtEUR(q.total_range.min)}–${fmtEUR(q.total_range.max)}`
                            : fmtEUR(q.total_range?.max ?? q.total)}
                      </div>
                      <div className="text-[10px] text-zinc-500">{new Date(q.updated_at).toLocaleDateString("pt-BR")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Quotes;
